import { useMutation, useQueryClient } from "@tanstack/react-query";
import { recordInstanceKeys } from "../record/record.options";
import { jobKeys } from "./job.options";
import { jobService } from "./job.service";
import type {
	CreateJobInfo,
	CreateJobTypeInfo,
	EditJobTypeInfo,
	JobParams,
	JobTypesQueryOptionsInput,
	ServiceResult,
	UpdateJobInfo,
	UpdateJobStatusInfo,
} from "./job.types";

// ==================== CORE JOB MUTATIONS ====================

/**
 * Create a new job with enhanced error handling and optimistic updates
 */
export const useCreateJob = (pageSize: number, pageIndex: number) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: CreateJobInfo) => jobService.createJob(data),
		onMutate: async (newJob) => {
			console.log("onMutate newJob: ", newJob);
			// Cancel any outgoing refetches
			await queryClient.cancelQueries({
				queryKey: jobKeys.jobs(),
			});

			// Snapshot the previous value
			const previousJobs = queryClient.getQueriesData({
				queryKey: jobKeys.jobs(),
			});

			// Optimistically update the cache
			queryClient.setQueriesData(
				{ queryKey: jobKeys.jobsList(pageSize, pageIndex) },
				(old: unknown) => {
					if (!old || typeof old !== "object") return old;
					const oldData = old as { data?: unknown[] };
					return {
						...oldData,
						data: [
							...(oldData.data || []),
							{
								id: `temp-${Date.now()}`,
								statusCode: 1,
								createdAt: new Date().toISOString(),
								...newJob,
							},
						],
					};
				},
			);

			return { previousJobs };
		},
		onSuccess: (result: ServiceResult) => {
			// Invalidate jobs list queries
			queryClient.invalidateQueries({
				queryKey: jobKeys.jobs(),
			});

			// If we have the affected ID, we can be more specific
			if (result.affectedId) {
				// The new job should be refetched when accessed
				queryClient.invalidateQueries({
					queryKey: jobKeys.jobsDetail(result.affectedId),
				});
			}
			return result;
		},
		onError: (error, _newJob, context) => {
			// Revert optimistic update on error
			if (context?.previousJobs) {
				context.previousJobs.forEach(([queryKey, data]) => {
					queryClient.setQueryData(queryKey, data);
				});
			}

			// Enhanced error handling with user-friendly messages
			console.error("Failed to create job:", error);
		},
		onSettled: () => {
			// Always refetch after error or success
			queryClient.invalidateQueries({
				queryKey: jobKeys.jobs(),
			});
		},
	});
};

// ==================== UPDATE JOB MUTATION ====================

/**
 * Update a job with cache invalidation
 */
export const useUpdateJob = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: UpdateJobInfo }) =>
			jobService.updateJob(id, data),
		onSuccess: (result: ServiceResult, { id }) => {
			// Do not await invalidation — form/details autosave shows Saved only after
			// mutateAsync settles. Blocking on detail/assignment refetches left fields
			// stuck on "Saving" (URL, select, datetime, etc.). Call sites that need
			// fresh lists (e.g. assignees picker) already refetchQueries explicitly.
			void Promise.all([
				queryClient.invalidateQueries({
					queryKey: jobKeys.jobsDetail(id),
				}),
				queryClient.invalidateQueries({
					queryKey: jobKeys.jobAssignments(id),
				}),
				queryClient.invalidateQueries({
					queryKey: jobKeys.jobSubContractors(id),
				}),
				queryClient.invalidateQueries({
					queryKey: jobKeys.jobs(),
				}),
			]);

			return result;
		},
		onError: (error) => {
			console.error("Failed to update job:", error);
		},
	});
};

// ==================== UPDATE JOB STATUS MUTATION ====================

/**
 * Update a job's status with cache invalidation
 */
export const useUpdateJobStatus = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: UpdateJobStatusInfo }) =>
			jobService.updateJobStatus(id, data),
		onSuccess: (result: ServiceResult, { id }) => {
			// Invalidate the specific job detail query
			queryClient.invalidateQueries({
				queryKey: jobKeys.jobsDetail(id),
			});

			// Invalidate all job list queries
			queryClient.invalidateQueries({
				queryKey: jobKeys.jobs(),
			});

			// Invalidate valid statuses for this job (status change may affect what's valid)
			queryClient.invalidateQueries({
				queryKey: jobKeys.jobsValidStatuses(id),
			});

			return result;
		},
		onError: (error) => {
			console.error("Failed to update job status:", error);
		},
	});
};

// ==================== DELETE JOB MUTATION ====================

/**
 * Delete a job with cache invalidation.
 * Pass `organizationId` (from the route) so platform admins scope the DELETE correctly.
 */
export const useDeleteJob = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			id,
			organizationId,
		}: {
			id: string;
			organizationId?: string;
		}) => jobService.deleteJob(id, { organizationId }),
		onSuccess: (_result, { id }) => {
			queryClient.invalidateQueries({
				queryKey: jobKeys.jobsDetail(id),
			});
			queryClient.invalidateQueries({
				queryKey: jobKeys.jobs(),
			});
			queryClient.invalidateQueries({
				queryKey: jobKeys.jobAssignments(id),
			});
			queryClient.invalidateQueries({
				queryKey: jobKeys.jobSubContractors(id),
			});
		},
		onError: (error) => {
			console.error("Failed to delete job:", error);
		},
	});
};

// ==================== EXPORT MUTATION ====================

/**
 * Export jobs to CSV
 */
export const useExportJobs = () => {
	return useMutation({
		mutationFn: async ({
			startIndex,
			count,
			params,
		}: {
			startIndex: number;
			count: number;
			params: JobParams & { columns?: string };
		}) => {
			const blob = await jobService.exportJobs(startIndex, count, params);
			return blob;
		},
		onError: (error) => {
			console.error("Export failed:", error);
		},
	});
};

export const useExportJobTypes = () => {
	return useMutation({
		mutationFn: async ({
			startIndex,
			count,
			params,
		}: {
			startIndex: number;
			count: number;
			params: JobTypesQueryOptionsInput & { columns?: string };
		}) => {
			const { columns, ...filters } = params;
			return await jobService.exportJobTypesCsv(
				startIndex,
				count,
				filters,
				columns,
			);
		},
		onError: (error) => {
			console.error("Job types export failed:", error);
		},
	});
};

// ==================== CREATE JOB TYPE MUTATION ====================

/**
 * Create a new job type with cache invalidation
 */
export const useCreateJobType = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (
			payload:
				| CreateJobTypeInfo
				| { data: CreateJobTypeInfo; organizationId?: string },
		) => {
			const data = "data" in payload ? payload.data : payload;
			const organizationId =
				"data" in payload
					? payload.organizationId
					: (payload.organizationId ?? undefined);
			return jobService.createJobType(data, { organizationId });
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [...jobKeys.jobs(), "job-types"],
			});
		},
		onError: (error) => {
			console.error("Failed to create job type:", error);
		},
	});
};

/**
 * Duplicate a job type. Cache refresh matches create; navigation stays with the caller.
 */
export const useDuplicateJobType = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			jobTypeId,
			organizationId,
		}: {
			jobTypeId: string;
			organizationId?: string;
		}) => jobService.duplicateJobType(jobTypeId, { organizationId }),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [...jobKeys.jobs(), "job-types"],
			});
		},
		onError: (error) => {
			console.error("Failed to duplicate job type:", error);
		},
	});
};

export const useUpdateJobType = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			jobTypeId,
			data,
			organizationId,
		}: {
			jobTypeId: string;
			data: EditJobTypeInfo;
			organizationId?: string;
		}) => jobService.updateJobType(jobTypeId, data, { organizationId }),
		onSuccess: (_result, { jobTypeId }) => {
			queryClient.invalidateQueries({
				queryKey: [...jobKeys.jobs(), "job-types"],
			});
			queryClient.invalidateQueries({
				queryKey: [...jobKeys.jobs(), "job-type-detail", jobTypeId],
			});
			queryClient.invalidateQueries({
				queryKey: jobKeys.jobTypeHistory(jobTypeId),
			});
		},
		onError: (error) => {
			console.error("Failed to update job type:", error);
		},
	});
};

export const useEnableJobType = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({
			jobTypeId,
			organizationId,
		}: {
			jobTypeId: string;
			organizationId?: string;
		}) => jobService.enableJobType(jobTypeId, { organizationId }),
		onSuccess: (_result, { jobTypeId }) => {
			queryClient.invalidateQueries({
				queryKey: [...jobKeys.jobs(), "job-types"],
			});
			queryClient.invalidateQueries({
				queryKey: [...jobKeys.jobs(), "job-type-detail", jobTypeId],
			});
			queryClient.invalidateQueries({
				queryKey: jobKeys.jobTypeHistory(jobTypeId),
			});
		},
		onError: (error) => {
			console.error("Failed to enable job type:", error);
		},
	});
};

export const useDisableJobType = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({
			jobTypeId,
			organizationId,
		}: {
			jobTypeId: string;
			organizationId?: string;
		}) => jobService.disableJobType(jobTypeId, { organizationId }),
		onSuccess: (_result, { jobTypeId }) => {
			queryClient.invalidateQueries({
				queryKey: [...jobKeys.jobs(), "job-types"],
			});
			queryClient.invalidateQueries({
				queryKey: [...jobKeys.jobs(), "job-type-detail", jobTypeId],
			});
			queryClient.invalidateQueries({
				queryKey: jobKeys.jobTypeHistory(jobTypeId),
			});
		},
		onError: (error) => {
			console.error("Failed to disable job type:", error);
		},
	});
};

export const useEnableJobTypeSubOrganization = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({
			typeId,
			subOrgId,
			organizationId,
		}: {
			typeId: string;
			subOrgId: string;
			organizationId?: string;
		}) =>
			jobService.enableJobTypeSubOrganization(typeId, subOrgId, {
				organizationId,
			}),
		onSuccess: (_result, { typeId }) => {
			queryClient.invalidateQueries({
				queryKey: [...jobKeys.jobs(), "job-types"],
			});
			queryClient.invalidateQueries({
				queryKey: [...jobKeys.jobs(), "job-type-detail", typeId],
			});
			queryClient.invalidateQueries({
				queryKey: jobKeys.jobTypeHistory(typeId),
			});
		},
		onError: (error) => {
			console.error("Failed to enable sub-organization for job type:", error);
		},
	});
};

export const useDisableJobTypeSubOrganization = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({
			typeId,
			subOrgId,
			organizationId,
		}: {
			typeId: string;
			subOrgId: string;
			organizationId?: string;
		}) =>
			jobService.disableJobTypeSubOrganization(typeId, subOrgId, {
				organizationId,
			}),
		onSuccess: (_result, { typeId }) => {
			queryClient.invalidateQueries({
				queryKey: [...jobKeys.jobs(), "job-types"],
			});
			queryClient.invalidateQueries({
				queryKey: [...jobKeys.jobs(), "job-type-detail", typeId],
			});
			queryClient.invalidateQueries({
				queryKey: jobKeys.jobTypeHistory(typeId),
			});
		},
		onError: (error) => {
			console.error("Failed to disable sub-organization for job type:", error);
		},
	});
};

export const useDeleteJobType = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			id,
			organizationId,
		}: {
			id: string;
			organizationId?: string;
		}) => jobService.deleteJobType(id, { organizationId }),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [...jobKeys.jobs(), "job-types"],
			});
			queryClient.invalidateQueries({
				queryKey: [...jobKeys.jobs(), "job-type-detail"],
			});
		},
		onError: (error) => {
			console.error("Failed to delete job type:", error);
		},
	});
};

// ==================== JOB ASSET / RECORD ASSOCIATION MUTATIONS ====================

/**
 * Link an asset to a job.
 * POST /jobs/{jobId}/assets/{assetId}
 */
export const useLinkJobAsset = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			jobId,
			assetId,
			organizationId,
		}: {
			jobId: string;
			assetId: string;
			organizationId?: string;
		}) => jobService.linkJobAsset(jobId, assetId, { organizationId }),
		onSuccess: (_result, { jobId }) => {
			void Promise.all([
				queryClient.invalidateQueries({ queryKey: jobKeys.jobAssets(jobId) }),
				queryClient.invalidateQueries({ queryKey: jobKeys.jobsDetail(jobId) }),
				queryClient.invalidateQueries({ queryKey: ["asset-instance"] }),
			]);
		},
	});
};

/**
 * Unlink an asset from a job.
 * DELETE /jobs/{jobId}/assets/{assetId}
 */
export const useUnlinkJobAsset = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			jobId,
			assetId,
			organizationId,
		}: {
			jobId: string;
			assetId: string;
			organizationId?: string;
		}) => jobService.unlinkJobAsset(jobId, assetId, { organizationId }),
		onSuccess: (_result, { jobId }) => {
			void Promise.all([
				queryClient.invalidateQueries({ queryKey: jobKeys.jobAssets(jobId) }),
				queryClient.invalidateQueries({ queryKey: jobKeys.jobsDetail(jobId) }),
				queryClient.invalidateQueries({ queryKey: ["asset-instance"] }),
			]);
		},
	});
};

/**
 * Link a record to a job.
 * POST /jobs/{jobId}/records/{recordId}
 */
export const useLinkJobRecord = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			jobId,
			recordId,
			organizationId,
		}: {
			jobId: string;
			recordId: string;
			organizationId?: string;
		}) => jobService.linkJobRecord(jobId, recordId, { organizationId }),
		onSuccess: (_result, { jobId }) => {
			void Promise.all([
				queryClient.invalidateQueries({ queryKey: jobKeys.jobRecords(jobId) }),
				queryClient.invalidateQueries({ queryKey: jobKeys.jobsDetail(jobId) }),
				queryClient.invalidateQueries({
					queryKey: recordInstanceKeys.lists(),
				}),
				queryClient.invalidateQueries({
					queryKey: recordInstanceKeys.counts(),
				}),
			]);
		},
	});
};

/**
 * Unlink a record from a job.
 * DELETE /jobs/{jobId}/records/{recordId}
 */
export const useUnlinkJobRecord = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			jobId,
			recordId,
			organizationId,
		}: {
			jobId: string;
			recordId: string;
			organizationId?: string;
		}) => jobService.unlinkJobRecord(jobId, recordId, { organizationId }),
		onSuccess: (_result, { jobId }) => {
			void Promise.all([
				queryClient.invalidateQueries({ queryKey: jobKeys.jobRecords(jobId) }),
				queryClient.invalidateQueries({ queryKey: jobKeys.jobsDetail(jobId) }),
				queryClient.invalidateQueries({
					queryKey: recordInstanceKeys.lists(),
				}),
				queryClient.invalidateQueries({
					queryKey: recordInstanceKeys.counts(),
				}),
			]);
		},
	});
};

// ==================== JOB FILE MUTATIONS ====================

/**
 * Upload a job attachment.
 * POST /jobs/{jobId}/files
 */
export const useUploadJobFile = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			jobId,
			file,
			organizationId,
			description,
			entityAttachmentRequirementTagId,
			onUploadProgress,
		}: {
			jobId: string;
			file: File | Blob;
			organizationId?: string;
			description?: string;
			entityAttachmentRequirementTagId?: string | null;
			onUploadProgress?: (percent: number) => void;
		}) =>
			jobService.uploadJobFile(jobId, file, {
				organizationId,
				description,
				entityAttachmentRequirementTagId,
				onUploadProgress,
			}),
		onSuccess: (_result, { jobId }) => {
			queryClient.invalidateQueries({
				queryKey: jobKeys.jobFiles(jobId),
			});
		},
	});
};

/**
 * Delete a job attachment.
 * DELETE /jobs/{jobId}/files/{fileId}
 */
export const useDeleteJobFile = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			jobId,
			fileId,
			organizationId,
		}: {
			jobId: string;
			fileId: string;
			organizationId?: string;
		}) => jobService.deleteJobFile(jobId, fileId, { organizationId }),
		onSuccess: (_result, { jobId }) => {
			queryClient.invalidateQueries({
				queryKey: jobKeys.jobFiles(jobId),
			});
		},
	});
};

/**
 * Rename / update a job attachment.
 * PUT /jobs/{jobId}/files/{jobFileId}
 */
export const useEditJobFile = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			jobId,
			fileId,
			name,
			description,
			organizationId,
		}: {
			jobId: string;
			fileId: string;
			name?: string | null;
			description?: string | null;
			organizationId?: string;
		}) =>
			jobService.editJobFile(
				jobId,
				fileId,
				{ name, description },
				{ organizationId },
			),
		onSuccess: (_result, { jobId }) => {
			queryClient.invalidateQueries({
				queryKey: jobKeys.jobFiles(jobId),
			});
		},
	});
};

// ==================== JOB NOTE MUTATIONS ====================

/**
 * Create a job note.
 * POST /jobs/{jobId}/notes
 */
export const useCreateJobNote = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			jobId,
			text,
			organizationId,
		}: {
			jobId: string;
			text: string;
			organizationId?: string;
		}) => jobService.createJobNote(jobId, { text }, { organizationId }),
		onSuccess: (_result, { jobId }) => {
			queryClient.invalidateQueries({
				queryKey: jobKeys.jobNotes(jobId),
			});
		},
	});
};

/**
 * Update a job note.
 * PUT /jobs/{jobId}/notes/{noteId}
 */
export const useUpdateJobNote = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			jobId,
			noteId,
			text,
			organizationId,
		}: {
			jobId: string;
			noteId: string;
			text: string;
			organizationId?: string;
		}) => jobService.updateJobNote(jobId, noteId, { text }, { organizationId }),
		onSuccess: (_result, { jobId }) => {
			queryClient.invalidateQueries({
				queryKey: jobKeys.jobNotes(jobId),
			});
		},
	});
};

/**
 * Delete a job note.
 * DELETE /jobs/{jobId}/notes/{noteId}
 */
export const useDeleteJobNote = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			jobId,
			noteId,
			organizationId,
		}: {
			jobId: string;
			noteId: string;
			organizationId?: string;
		}) => jobService.deleteJobNote(jobId, noteId, { organizationId }),
		onSuccess: (_result, { jobId }) => {
			queryClient.invalidateQueries({
				queryKey: jobKeys.jobNotes(jobId),
			});
		},
	});
};

// ==================== GROUPED EXPORTS ====================

/**
 * All job mutations grouped by category
 */
export const jobMutations = {
	// Core job mutations
	create: useCreateJob,
	createJobType: useCreateJobType,
	duplicateJobType: useDuplicateJobType,
	updateJobType: useUpdateJobType,
	deleteJobType: useDeleteJobType,
	update: useUpdateJob,
	updateStatus: useUpdateJobStatus,
	delete: useDeleteJob,
	exportJobs: useExportJobs,
	exportJobTypes: useExportJobTypes,
	linkAsset: useLinkJobAsset,
	unlinkAsset: useUnlinkJobAsset,
	linkRecord: useLinkJobRecord,
	unlinkRecord: useUnlinkJobRecord,
	uploadFile: useUploadJobFile,
	deleteFile: useDeleteJobFile,
	editFile: useEditJobFile,
	createNote: useCreateJobNote,
	updateNote: useUpdateJobNote,
	deleteNote: useDeleteJobNote,
} as const;
